import { DIALOG_COLORS, Badge } from "../../../../components/ui";
import { useCallback, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { brand } from "../../../../config/theme";
import Modal from "../../../../components/base/Modal";
import { buildThemeClasses } from "../../theme";
import {
  fetchBookings,
  fetchBlocks,
  getAvailability,
  createBooking,
  updateBooking,
  deleteBooking,
  createBlock,
  deleteBlock,
  tzParts,
  fmtTime,
  fmtDateLong,
  dateKey,
  BOOKING_STATUS_META,
  WEEKDAYS_ES,
  MONTHS_ES,
  type Booking,
  type TimeBlock,
  type Availability,
  type BookingStatus,
} from "../../services/bookingsService";
import { phoneChannels } from "../../services/leadPhone";
import {
  MonthGrid,
  TimeGrid,
  keyToUTC,
  utcToKey,
  addDaysKey,
  weekdayOfKey,
  weekKeys,
  formatMinInput,
  addMinutesToInput,
  type View,
  type CalEvent,
} from "../../components/CalendarGrids";

// ===========================================================================
// Agenda — LIVE bookings calendar. Month / week / day views of booked meetings
// and time blocks. The user can drop a booking or block time on any slot so no
// call lands during focus time. Reads/writes the crmBookings API; renders
// wall-clock in the configured business timezone.
// ===========================================================================

export default function AgendaCalendar() {
  const tc = buildThemeClasses();

  const [availability, setAvailability] = useState<Availability | null>(null);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [blocks, setBlocks] = useState<TimeBlock[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [view, setView] = useState<View>("month");
  const [refKey, setRefKey] = useState<string>(() => utcToKey(new Date()));
  const [selected, setSelected] = useState<CalEvent | null>(null);
  const [composer, setComposer] = useState<{ kind: "booking" | "block"; startKey: string; startMin: number } | null>(null);

  const tz = availability?.timezone || "America/La_Paz";
  const offset = availability?.utcOffset || "-04:00";
  const slotMinutes = availability?.slotMinutes || 30;
  const todayKey = useMemo(() => dateKey(new Date(), tz), [tz]);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const [av, bk, bl] = await Promise.all([getAvailability(), fetchBookings(), fetchBlocks()]);
      if (av) setAvailability(av);
      setBookings(bk);
      setBlocks(bl);
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      setError(err.response?.data?.message || err.message || "No se pudo cargar la agenda");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Build the unified events list (filtered to what the current tz buckets).
  const events = useMemo<CalEvent[]>(() => {
    const out: CalEvent[] = [];
    for (const b of bookings) {
      if (!b.start || !b.end) continue;
      const s = tzParts(new Date(b.start), tz);
      const e = tzParts(new Date(b.end), tz);
      out.push({
        id: b.id,
        kind: "booking",
        title: b.title || b.name || "Reserva",
        startKey: s.dateKey,
        startMin: s.hour * 60 + s.minute,
        endMin: e.dateKey === s.dateKey ? e.hour * 60 + e.minute : 24 * 60,
        status: b.status,
        booking: b,
      });
    }
    for (const bl of blocks) {
      if (!bl.start || !bl.end) continue;
      const s = tzParts(new Date(bl.start), tz);
      const e = tzParts(new Date(bl.end), tz);
      out.push({
        id: bl.id,
        kind: "block",
        title: bl.title || "Bloqueo",
        startKey: s.dateKey,
        startMin: bl.allDay ? 0 : s.hour * 60 + s.minute,
        endMin: bl.allDay ? 24 * 60 : e.dateKey === s.dateKey ? e.hour * 60 + e.minute : 24 * 60,
        block: bl,
      });
    }
    return out;
  }, [bookings, blocks, tz]);

  const eventsByDay = useMemo(() => {
    const map: Record<string, CalEvent[]> = {};
    for (const ev of events) {
      (map[ev.startKey] ||= []).push(ev);
    }
    for (const k of Object.keys(map)) map[k].sort((a, b) => a.startMin - b.startMin);
    return map;
  }, [events]);

  // Visible hour range for week/day, derived from availability windows + events.
  const [dayStartH, dayEndH] = useMemo(() => {
    let minS = 8 * 60;
    let maxE = 18 * 60;
    const weekly = availability?.weekly || {};
    for (const wins of Object.values(weekly)) {
      for (const w of wins) {
        const [sh, sm] = w.start.split(":").map(Number);
        const [eh, em] = w.end.split(":").map(Number);
        minS = Math.min(minS, sh * 60 + sm);
        maxE = Math.max(maxE, eh * 60 + em);
      }
    }
    for (const ev of events) {
      if (ev.kind === "block" && ev.block?.allDay) continue;
      minS = Math.min(minS, ev.startMin);
      maxE = Math.max(maxE, ev.endMin);
    }
    return [Math.max(0, Math.floor(minS / 60)), Math.min(24, Math.ceil(maxE / 60))];
  }, [availability, events]);

  // --- navigation -----------------------------------------------------------
  const move = (dir: number) => {
    if (view === "month") {
      const [y, m] = refKey.split("-").map(Number);
      const d = new Date(Date.UTC(y, m - 1 + dir, 1, 12));
      setRefKey(utcToKey(d));
    } else if (view === "week") {
      setRefKey(addDaysKey(refKey, dir * 7));
    } else {
      setRefKey(addDaysKey(refKey, dir));
    }
  };
  const goToday = () => setRefKey(utcToKey(new Date()));

  const headerLabel = useMemo(() => {
    const [y, m] = refKey.split("-").map(Number);
    if (view === "month") return `${MONTHS_ES[m - 1]} ${y}`;
    if (view === "day") {
      const p = keyToUTC(refKey);
      return `${WEEKDAYS_ES[p.getUTCDay()]} ${p.getUTCDate()} ${MONTHS_ES[p.getUTCMonth()]} ${p.getUTCFullYear()}`;
    }
    const wk = weekKeys(refKey);
    const a = keyToUTC(wk[0]);
    const b = keyToUTC(wk[6]);
    return `${a.getUTCDate()} ${MONTHS_ES[a.getUTCMonth()].slice(0, 3)} – ${b.getUTCDate()} ${MONTHS_ES[b.getUTCMonth()].slice(0, 3)} ${b.getUTCFullYear()}`;
  }, [refKey, view]);

  // --- availability shading (week/day) --------------------------------------
  const isWithinAvailability = useCallback(
    (key: string, minute: number) => {
      const wins = availability?.weekly?.[String(weekdayOfKey(key))] || [];
      return wins.some((w) => {
        const [sh, sm] = w.start.split(":").map(Number);
        const [eh, em] = w.end.split(":").map(Number);
        return minute >= sh * 60 + sm && minute < eh * 60 + em;
      });
    },
    [availability],
  );

  // --- counters for the header ---------------------------------------------
  const stats = useMemo(() => {
    const upcoming = bookings.filter(
      (b) => b.status !== "cancelled" && new Date(b.start) >= new Date(),
    );
    return {
      upcoming: upcoming.length,
      requested: bookings.filter((b) => b.status === "requested").length,
      blocks: blocks.length,
    };
  }, [bookings, blocks]);

  const openSlot = (kind: "booking" | "block", startKey: string, startMin: number) =>
    setComposer({ kind, startKey, startMin });

  return (
    <div className="h-full w-full overflow-y-auto p-6">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-500/15 text-primary-500">
              <i className="ri-calendar-2-line text-xl" />
            </span>
            <div>
              <h1 className={`text-xl font-bold ${tc.text}`}>Calendario de reservas</h1>
              <p className={`text-sm ${tc.textSecondary}`}>
                Reuniones agendadas y tiempo bloqueado · zona horaria {tz}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => openSlot("block", todayKey, 9 * 60)}
              className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium ${tc.border} ${tc.cardBg} ${tc.text} hover:border-primary-500`}
            >
              <i className="ri-lock-line" /> Bloquear tiempo
            </button>
            <button
              onClick={() => openSlot("booking", todayKey, 9 * 60)}
              className="flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-on-primary transition-colors hover:bg-primary-400"
            >
              <i className="ri-add-line" /> Nueva reserva
            </button>
          </div>
        </div>

        {/* Stat chips */}
        <div className="mb-5 grid grid-cols-3 gap-3">
          <Stat tc={tc} icon="ri-calendar-check-line" label="Próximas" value={stats.upcoming} />
          <Stat tc={tc} icon="ri-time-line" label="Por confirmar" value={stats.requested} tone="text-amber-500" />
          <Stat tc={tc} icon="ri-lock-line" label="Bloqueos" value={stats.blocks} tone="text-gray-400" />
        </div>

        {/* Toolbar */}
        <div className={`mb-3 flex flex-wrap items-center justify-between gap-3 rounded-xl border p-2 ${tc.cardBorder} ${tc.cardBg}`}>
          <div className="flex items-center gap-1">
            <button onClick={() => move(-1)} className={`rounded-lg p-2 ${tc.buttonHover} ${tc.text}`} title="Anterior">
              <i className="ri-arrow-left-s-line" />
            </button>
            <button onClick={goToday} className={`rounded-lg border px-3 py-1.5 text-sm font-medium ${tc.border} ${tc.text} hover:border-primary-500`}>
              Hoy
            </button>
            <button onClick={() => move(1)} className={`rounded-lg p-2 ${tc.buttonHover} ${tc.text}`} title="Siguiente">
              <i className="ri-arrow-right-s-line" />
            </button>
            <span className={`ml-2 text-sm font-semibold capitalize ${tc.text}`}>{headerLabel}</span>
          </div>
          <div className="flex items-center gap-1 rounded-lg border p-0.5">
            {(["month", "week", "day"] as View[]).map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  view === v ? "bg-primary-500 text-white" : `${tc.textSecondary} hover:text-primary-500`
                }`}
              >
                {v === "month" ? "Mes" : v === "week" ? "Semana" : "Día"}
              </button>
            ))}
          </div>
        </div>

        {/* Calendar body */}
        <div className={`rounded-2xl border ${tc.cardBorder} ${tc.cardBg}`}>
          {loading ? (
            <div className={`p-12 text-center text-sm ${tc.textSecondary}`}>
              <i className="ri-loader-4-line mr-2 animate-spin" /> Cargando agenda...
            </div>
          ) : error ? (
            <div className="p-12 text-center">
              <i className="ri-error-warning-line mb-2 text-3xl text-red-500" />
              <p className="text-sm text-red-500">{error}</p>
              <button onClick={() => load()} className="mt-3 rounded-lg bg-primary-500 px-4 py-2 text-sm text-on-primary hover:bg-primary-400">
                Reintentar
              </button>
            </div>
          ) : view === "month" ? (
            <MonthGrid
              refKey={refKey}
              todayKey={todayKey}
              eventsByDay={eventsByDay}
              tc={tc}
              onPickDay={(k) => {
                setRefKey(k);
                setView("day");
              }}
              onSelect={setSelected}
            />
          ) : (
            <TimeGrid
              keys={view === "week" ? weekKeys(refKey) : [refKey]}
              dayStartH={dayStartH}
              dayEndH={dayEndH}
              todayKey={todayKey}
              eventsByDay={eventsByDay}
              isWithinAvailability={isWithinAvailability}
              tc={tc}
              onEmptyClick={(k, min) => openSlot("booking", k, min)}
              onSelect={setSelected}
            />
          )}
        </div>

        <p className={`mt-3 text-xs ${tc.textSecondary}`}>
          <i className="ri-information-line mr-1" />
          Las reservas del sitio web entran como “Solicitada”. Cuando Google Calendar está conectado se crea el evento y el enlace de la reunión automáticamente.
        </p>
      </div>

      {selected && (
        <EventDetail
          event={selected}
          tc={tc}
          tz={tz}
          onClose={() => setSelected(null)}
          onChanged={() => {
            setSelected(null);
            load(true);
          }}
        />
      )}

      {composer && (
        <Composer
          kind={composer.kind}
          startKey={composer.startKey}
          startMin={composer.startMin}
          slotMinutes={slotMinutes}
          offset={offset}
          tz={tz}
          tc={tc}
          onClose={() => setComposer(null)}
          onCreated={() => {
            setComposer(null);
            load(true);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Event detail / quick actions
// ---------------------------------------------------------------------------
function EventDetail({
  event,
  tc,
  tz,
  onClose,
  onChanged,
}: {
  event: CalEvent;
  tc: ReturnType<typeof buildThemeClasses>;
  tz: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);

  const setStatus = async (status: BookingStatus) => {
    if (!event.booking) return;
    setBusy(true);
    try {
      await updateBooking(event.booking.id, { status });
      onChanged();
    } catch {
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo actualizar", confirmButtonColor: brand.primary });
      setBusy(false);
    }
  };

  const remove = async () => {
    const res = await Swal.fire({
      icon: "warning",
      title: event.kind === "block" ? "Eliminar bloqueo" : "Eliminar reserva",
      text: "Esta acción no se puede deshacer.",
      showCancelButton: true,
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: DIALOG_COLORS.danger,
    });
    if (!res.isConfirmed) return;
    setBusy(true);
    try {
      if (event.kind === "block") await deleteBlock(event.id);
      else await deleteBooking(event.id);
      onChanged();
    } catch {
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo eliminar", confirmButtonColor: brand.primary });
      setBusy(false);
    }
  };

  const b = event.booking;
  return (
    <Modal
      title={event.kind === "block" ? "Bloqueo de tiempo" : "Detalle de reserva"}
      themeClasses={tc}
      onClose={onClose}
      width="max-w-lg"
      content={
        <div className="space-y-3 text-sm">
          <div className="flex items-center gap-2">
            <i className={`${event.kind === "block" ? "ri-lock-line text-gray-400" : "ri-calendar-check-line text-primary-500"} text-lg`} />
            <span className={`font-semibold ${tc.text}`}>{event.title}</span>
          </div>
          <p className={tc.textSecondary}>
            <i className="ri-time-line mr-1" />
            {fmtDateLong((b ?? event.block)!.start, tz)} · {fmtTime((b ?? event.block)!.start, tz)}–{fmtTime((b ?? event.block)!.end, tz)}
          </p>

          {b && (
            <>
              {b.status && (
                <Badge tone={BOOKING_STATUS_META[b.status].tone}>
                  {BOOKING_STATUS_META[b.status].label}
                </Badge>
              )}
              <div className={`grid grid-cols-2 gap-2 rounded-xl border p-3 ${tc.border}`}>
                <Field tc={tc} label="Contacto" value={b.name} />
                <Field tc={tc} label="Empresa" value={b.company} />
                <Field tc={tc} label="Correo" value={b.email} />
                <Field tc={tc} label="WhatsApp" value={b.whatsapp} />
                {/* Sólo cuando el prospecto dijo que su número NO tiene WhatsApp. */}
                {b.phone && <Field tc={tc} label="Teléfono (sin WhatsApp)" value={b.phone} />}
              </div>
              {b.notes && <p className={`text-sm ${tc.text}`}>{b.notes}</p>}
              {b.meetingUrl && (
                <a href={b.meetingUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-lg bg-emerald-500/15 px-3 py-2 text-sm font-medium text-emerald-600 hover:bg-emerald-500/25">
                  <i className="ri-vidicon-line" /> Unirse a la reunión
                </a>
              )}
              {b.source === "landing" && (
                <p className={`text-xs ${tc.textSecondary}`}>
                  <i className="ri-global-line mr-1" /> Reservada desde el sitio web
                </p>
              )}

              {/* status quick actions */}
              <div className="flex flex-wrap gap-2 pt-1">
                {b.status !== "confirmed" && (
                  <button disabled={busy} onClick={() => setStatus("confirmed")} className="rounded-lg bg-emerald-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-400 disabled:opacity-60">
                    Confirmar
                  </button>
                )}
                {b.status !== "completed" && (
                  <button disabled={busy} onClick={() => setStatus("completed")} className="rounded-lg bg-sky-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-sky-400 disabled:opacity-60">
                    Completada
                  </button>
                )}
                {b.status !== "no_show" && (
                  <button disabled={busy} onClick={() => setStatus("no_show")} className="rounded-lg bg-red-500/90 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-500 disabled:opacity-60">
                    No asistió
                  </button>
                )}
                {b.status !== "cancelled" && (
                  <button disabled={busy} onClick={() => setStatus("cancelled")} className="rounded-lg border px-3 py-1.5 text-xs font-medium text-gray-500 hover:bg-gray-500/10 disabled:opacity-60">
                    Cancelar
                  </button>
                )}
              </div>
            </>
          )}

          {event.block?.reason && <p className={`text-sm ${tc.text}`}>{event.block.reason}</p>}

          <div className="flex justify-end border-t pt-3">
            <button disabled={busy} onClick={remove} className="rounded-lg px-3 py-1.5 text-xs font-medium text-red-500 hover:bg-red-500/10 disabled:opacity-60">
              <i className="ri-delete-bin-line mr-1" /> Eliminar
            </button>
          </div>
        </div>
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Composer — create a booking or a time block from a clicked slot.
// ---------------------------------------------------------------------------
function Composer({
  kind,
  startKey,
  startMin,
  slotMinutes,
  offset,
  tz,
  tc,
  onClose,
  onCreated,
}: {
  kind: "booking" | "block";
  startKey: string;
  startMin: number;
  slotMinutes: number;
  offset: string;
  tz: string;
  tc: ReturnType<typeof buildThemeClasses>;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [mode, setMode] = useState<"booking" | "block">(kind);
  const [date, setDate] = useState(startKey);
  const [time, setTime] = useState(formatMinInput(startMin));
  const [duration, setDuration] = useState(mode === "block" ? 60 : slotMinutes);
  const [title, setTitle] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  // ONE number + "¿tiene WhatsApp?", exactly like the landing form: the answer
  // decides whether it is stored as `whatsapp` or as `phone`.
  const [phoneNumber, setPhoneNumber] = useState("");
  const [hasWhatsapp, setHasWhatsapp] = useState(true);
  const [company, setCompany] = useState("");
  const [notes, setNotes] = useState("");
  const [allDay, setAllDay] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const buildISO = (t: string) => `${date}T${t}:00${offset}`;

  const submit = async () => {
    setErr(null);
    setBusy(true);
    try {
      if (mode === "block") {
        const start = allDay ? `${date}T00:00:00${offset}` : buildISO(time);
        const end = allDay
          ? `${date}T23:59:00${offset}`
          : buildISO(addMinutesToInput(time, duration));
        await createBlock({ title: title || "Bloqueo", reason: notes, start, end, allDay });
      } else {
        if (!name && !email) {
          setErr("Indica al menos un nombre o correo.");
          setBusy(false);
          return;
        }
        const start = buildISO(time);
        const end = buildISO(addMinutesToInput(time, duration));
        await createBooking({
          title: title || `Reunión — ${name || email}`,
          type: "meeting",
          name,
          email,
          ...phoneChannels(phoneNumber, hasWhatsapp),
          company,
          notes,
          start,
          end,
          status: "confirmed",
          timezone: tz,
        });
      }
      onCreated();
    } catch (e) {
      const error = e as { response?: { data?: { data?: { message?: string }; message?: string } }; message?: string };
      setErr(error.response?.data?.data?.message || error.response?.data?.message || error.message || "No se pudo guardar");
      setBusy(false);
    }
  };

  return (
    <Modal
      title={mode === "block" ? "Bloquear tiempo" : "Nueva reserva"}
      themeClasses={tc}
      onClose={onClose}
      width="max-w-lg"
      content={
        <div className="space-y-3">
          {/* mode toggle */}
          <div className="flex gap-1 rounded-lg border p-0.5">
            {(["booking", "block"] as const).map((m) => (
              <button
                key={m}
                onClick={() => {
                  setMode(m);
                  setDuration(m === "block" ? 60 : slotMinutes);
                }}
                className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  mode === m ? "bg-primary-500 text-white" : `${tc.textSecondary} hover:text-primary-500`
                }`}
              >
                {m === "booking" ? "Reunión" : "Bloqueo"}
              </button>
            ))}
          </div>

          {err && <p className="rounded-lg bg-red-500/10 px-3 py-2 text-sm text-red-500">{err}</p>}

          <Input tc={tc} label="Título" value={title} onChange={setTitle} placeholder={mode === "block" ? "Tiempo personal" : "Demo de producto"} />

          {mode === "booking" && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <Input tc={tc} label="Nombre" value={name} onChange={setName} />
                <Input tc={tc} label="Empresa" value={company} onChange={setCompany} />
                <Input tc={tc} label="Correo" value={email} onChange={setEmail} type="email" />
                <Input tc={tc} label="Teléfono" value={phoneNumber} onChange={setPhoneNumber} placeholder="+52 55 1234 5678" />
              </div>
              <label className={`flex items-center gap-2 text-sm ${tc.text}`}>
                <input type="checkbox" checked={hasWhatsapp} onChange={(e) => setHasWhatsapp(e.target.checked)} />
                Este número tiene WhatsApp
              </label>
            </>
          )}

          <div className="grid grid-cols-3 gap-2">
            <Input tc={tc} label="Fecha" value={date} onChange={setDate} type="date" />
            {!allDay && <Input tc={tc} label="Hora" value={time} onChange={setTime} type="time" />}
            {!allDay && (
              <div>
                <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>Duración</label>
                <select
                  value={duration}
                  onChange={(e) => setDuration(Number(e.target.value))}
                  className={`w-full rounded-lg border px-2 py-2 text-sm ${tc.border} ${tc.cardBg} ${tc.text}`}
                >
                  {[15, 30, 45, 60, 90, 120].map((d) => (
                    <option key={d} value={d}>
                      {d} min
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {mode === "block" && (
            <label className={`flex items-center gap-2 text-sm ${tc.text}`}>
              <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} />
              Todo el día
            </label>
          )}

          <div>
            <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>{mode === "block" ? "Motivo" : "Notas"}</label>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className={`w-full rounded-lg border px-3 py-2 text-sm ${tc.border} ${tc.cardBg} ${tc.text}`}
            />
          </div>

          <div className="flex justify-end gap-2 border-t pt-3">
            <button onClick={onClose} className={`rounded-lg border px-4 py-2 text-sm ${tc.border} ${tc.text}`}>
              Cancelar
            </button>
            <button onClick={submit} disabled={busy} className="rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-on-primary hover:bg-primary-400 disabled:opacity-60">
              {busy ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </div>
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Small presentational helpers
// ---------------------------------------------------------------------------
function Stat({ tc, icon, label, value, tone }: { tc: ReturnType<typeof buildThemeClasses>; icon: string; label: string; value: number; tone?: string }) {
  return (
    <div className={`flex items-center gap-3 rounded-xl border p-3 ${tc.cardBorder} ${tc.cardBg}`}>
      <span className={`flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500/10 ${tone ?? "text-primary-500"}`}>
        <i className={icon} />
      </span>
      <div>
        <p className={`text-lg font-bold ${tc.text}`}>{value}</p>
        <p className={`text-xs ${tc.textSecondary}`}>{label}</p>
      </div>
    </div>
  );
}

function Field({ tc, label, value }: { tc: ReturnType<typeof buildThemeClasses>; label: string; value: string | null | undefined }) {
  return (
    <div>
      <p className={`text-[11px] ${tc.textSecondary}`}>{label}</p>
      <p className={`text-sm ${tc.text}`}>{value || "—"}</p>
    </div>
  );
}

function Input({
  tc,
  label,
  value,
  onChange,
  type = "text",
  placeholder,
}: {
  tc: ReturnType<typeof buildThemeClasses>;
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
}) {
  return (
    <div>
      <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>{label}</label>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full rounded-lg border px-3 py-2 text-sm ${tc.border} ${tc.cardBg} ${tc.text}`}
      />
    </div>
  );
}
